/**
 * FilterPanel.tsx
 *
 * Self-contained filter UI. Receives callbacks from useFilterState.
 * Renders in a collapsible sheet on mobile, always-visible sidebar on desktop.
 *
 * SECTIONS:
 *   1. Happening Now toggle (prominent)
 *   2. Dog Friendly toggle
 *   3. Day of week
 *   4. Time of day (range)
 *   5. Deal type
 *   6. Venue type / categories
 *   7. Neighborhood
 *   8. Price tier
 *   9. Clear all
 */

import React, { useState } from 'react'
import type { FilterState, DayOfWeek, DealType, VenueCategory } from '../types'
import {
  DAYS_OF_WEEK, DEAL_TYPE_LABELS, DEAL_TYPE_COLORS,
  CATEGORY_LABELS,
} from '../types'
import type { UseFilterStateReturn } from '../hooks/useFilterState'

import { countLiveNow } from '../utils/happeningNow'
import type { Venue } from '../types'



// ─────────────────────────────────────────────
// SMALL SHARED COMPONENTS
// ─────────────────────────────────────────────

function FilterChip({
  label,
  active,
  onClick,
  bg,
  color,
}: {
  label: string
  active: boolean
  onClick: () => void
  bg?: string
  color?: string
}) {
  return (
    <button
      className={`fp-chip${active ? ' active' : ''}`}
      onClick={onClick}
      style={active && bg ? { background: bg, borderColor: bg, color } : {}}
    >
      {label}
    </button>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <div className="fp-section-label">{children}</div>
}

// ─────────────────────────────────────────────
// MAIN FILTER PANEL
// ─────────────────────────────────────────────

interface FilterPanelProps extends UseFilterStateReturn {
  venues: Venue[]
  neighborhoods: string[]
}

export function FilterPanel({
  filters,
  sort,
  activeCount,
  venues,
  neighborhoods,
  toggleDay,
  toggleDealType,
  toggleCategory,
  toggleNeighborhood,
  setTimeWindow,
  setOpenNow,
  setDogFriendly,
  setSearch,
  clearAll,
}: FilterPanelProps) {
  const [showAllCategories, setShowAllCategories] = useState(false)

  const liveCount = countLiveNow(venues)

  // Rooftop + Patio shown by default; restaurant moved to overflow
  const PRIMARY_CATS: VenueCategory[] = [
    'rooftop', 'patio', 'sports_bar', 'dive_bar', 'cocktail_bar', 'brewery',
  ]
  const ALL_CATS = Object.keys(CATEGORY_LABELS) as VenueCategory[]
  const visibleCats = showAllCategories ? ALL_CATS : PRIMARY_CATS



  return (
    <div className="filter-panel">

      {/* ── SEARCH ── */}
      <div className="fp-search-row">
        <input
          className="fp-search"
          value={filters.search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search bars, deals, neighborhoods..."
          aria-label="Search"
        />
        {activeCount > 0 && (
          <button className="fp-clear-btn" onClick={clearAll} aria-label="Clear all filters">
            Clear {activeCount > 1 ? `(${activeCount})` : ''}
          </button>
        )}
      </div>

      {/* ── HAPPENING NOW ── */}
      <button
        className={`fp-now-toggle${filters.openNow ? ' active' : ''}`}
        onClick={() => setOpenNow(!filters.openNow)}
        aria-pressed={filters.openNow}
      >
        <span className={`fp-now-dot${filters.openNow ? ' pulse' : ''}`} />
        <span>Happening Now</span>
        {liveCount > 0 && <span className="fp-now-count">{liveCount}</span>}
      </button>

      {/* ── DOG FRIENDLY ── */}
      <button
        className={`fp-now-toggle${(filters as any).dogFriendly ? ' active' : ''}`}
        onClick={() => setDogFriendly(!(filters as any).dogFriendly)}
      >
        <span>🐾</span>
        <span>Dog Friendly Only</span>
      </button>

      {/* ── DAY ── */}
      <div className="fp-section">
        <SectionLabel>Day</SectionLabel>
        <div className="fp-chips">
          {DAYS_OF_WEEK.map(d => (
            <FilterChip
              key={d}
              label={d}
              active={filters.days.has(d)}
              onClick={() => toggleDay(d)}
            />
          ))}
        </div>
      </div>



      {/* ── DEAL TYPE ── */}
      <div className="fp-section">
        <SectionLabel>Deal type</SectionLabel>
        <div className="fp-chips">
          {(Object.entries(DEAL_TYPE_LABELS) as [DealType, string][]).map(([type, label]) => (
            <FilterChip
              key={type}
              label={label}
              active={filters.dealTypes.has(type)}
              onClick={() => toggleDealType(type)}
              bg={DEAL_TYPE_COLORS[type].bg}
              color={DEAL_TYPE_COLORS[type].text}
            />
          ))}
        </div>
      </div>

      {/* ── VENUE TYPE ── */}
      <div className="fp-section">
        <SectionLabel>Venue type</SectionLabel>
        <div className="fp-chips">
          {visibleCats.map(cat => (
            <FilterChip
              key={cat}
              label={CATEGORY_LABELS[cat]}
              active={filters.categories.has(cat)}
              onClick={() => toggleCategory(cat)}
            />
          ))}
          <button
            className="fp-chip fp-chip-more"
            onClick={() => setShowAllCategories(v => !v)}
          >
            {showAllCategories ? 'Less' : `+${ALL_CATS.length - PRIMARY_CATS.length} more`}
          </button>
        </div>
      </div>

      {/* ── NEIGHBORHOOD ── */}
      {neighborhoods.length > 0 && (
        <div className="fp-section">
          <SectionLabel>Neighborhood</SectionLabel>
          <div className="fp-chips">
            {neighborhoods.map(n => (
              <FilterChip
                key={n}
                label={n}
                active={filters.neighborhoods.has(n)}
                onClick={() => toggleNeighborhood(n)}
              />
            ))}
          </div>
        </div>
      )}



    </div>
  )
}
